
import React from 'react';
import { Avatar } from '@/components/ui/avatar';
import QRCode from 'react-qr-code';
import { ProfileData, AcademicData, SocialMedia, Position } from '@/types/profileTypes';
import { FlyerConfigFullOptions } from '@/types/flyerTypes';
import { capitalizeString, buildSocialMediaUrl } from '@/lib/utils';
import { parseLocalDate } from '@/utils/formatters';

interface FlyerTemplateProps {
  profileData: ProfileData & {
    academics?: any;
    social_media?: any[];
    press?: any[];
    experiences?: any[];
    events?: any[];
    preferences?: any;
    offers?: any[];
    academic_achievements?: any[];
    athletic_achievements?: any[];
    recommendations?: any[];
    media?: any[];
    high_school?: {
      name: string;
      city?: string;
      state?: string;
      type?: string;
    };
  };
  athleteStats: {
    combineScores?: any[];
    sportStats?: any[];
  };
  config: FlyerConfigFullOptions;
}

const FlyerTemplate: React.FC<FlyerTemplateProps> = ({
  profileData,
  athleteStats,
  config
}) => {
  if (!profileData) {
    return <div>No profile data available</div>;
  }

  // Default QR code URL if not specified in config
  const qrUrl = config.qrTargetUrl || `https://recruitlook.com/profile/${profileData.username || ''}`;

  // Helper to display values, using empty string instead of "N/A"
  const displayValue = (value: any, fallback = '') => {
    if (value === undefined || value === null) return fallback;
    return value;
  };

  // Type guards for complex union types
  const getSportName = () => {
    if (typeof profileData.sport === 'string') {
      return profileData.sport;
    } else if (profileData.sport && typeof profileData.sport === 'object') {
      return profileData.sport.name;
    }
    return '';
  };

  const getPositionName = () => {
    if (typeof profileData.primary_position === 'string') {
      return profileData.primary_position;
    } else if (profileData.primary_position && typeof profileData.primary_position === 'object') {
      return profileData.primary_position.position_name;
    }
    return '';
  };

  // Format height (in inches) to feet and inches
  const formatHeight = (heightInches: number | null | undefined) => {
    if (!heightInches) return '';
    const feet = Math.floor(heightInches / 12);
    const inches = heightInches % 12;
    return `${feet}'${inches}"`;
  };

  // Helper for getting high school name
  const getHighSchoolName = () => {
    if (profileData.high_school && profileData.high_school.name) {
      return profileData.high_school.name;
    }
    return '';
  };

  // Format stat names by replacing underscores with spaces and capitalizing each word
  const formatStatName = (statName: string): string => {
    if (!statName) return '';
    
    // Replace underscores with spaces
    const nameWithSpaces = statName.replace(/_/g, ' ');
    
    // Capitalize each word
    return nameWithSpaces
      .split(' ')
      .map(word => capitalizeString(word))
      .join(' ');
  };

  return (
    <div className="flyer-page bg-white max-w-[8.5in] mx-auto">
      {/* Header Section with Athlete Info - This section should never break */}
      <div className="flex items-start mb-6 pb-4 border-b print-block">
        <div className="flex-shrink-0 mr-6">
          {config.showAvatar !== false && profileData.avatar_url ? (
            <Avatar className="h-32 w-32">
              <img src={profileData.avatar_url} alt="Athlete" className="object-cover" />
            </Avatar>
          ) : (
            <div className="h-32 w-32 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-3xl font-bold text-gray-400">
                {profileData.name?.charAt(0) || ''}
              </span>
            </div>
          )}
        </div>
        
        <div className="flex-grow">
          <h1 className="text-2xl font-bold">{displayValue(profileData.name)}</h1>
          
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p>{displayValue(profileData.city)}, {displayValue(profileData.state)}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Sport</p>
              <p>{displayValue(getSportName())}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Position</p>
              <p>{displayValue(getPositionName())}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Graduation Year</p>
              <p>{displayValue(profileData.grad_year)}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Height</p>
              <p>{formatHeight(profileData.height_inches)}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Weight</p>
              <p>{profileData.weight_lbs ? `${profileData.weight_lbs} lbs` : ''}</p>
            </div>
            
            {profileData.about && (
              <div className="col-span-2">
                <p className="text-sm text-gray-500">About</p>
                <p className="text-sm">{displayValue(profileData.about)}</p>
              </div>
            )}
          </div>
        </div>
        
        {/* QR Code - Now using react-qr-code */}
        <div className="flex-shrink-0 ml-4">
          <div className="h-32 w-32 border-2 border-gray-300 flex items-center justify-center bg-white p-2">
            <QRCode 
              value={qrUrl}
              size={112}
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
              viewBox={`0 0 256 256`}
            />
          </div>
        </div>
      </div>

      {/* Personal Statement - Now full width above the two columns */}
      {profileData.personal_statement && (
        <div className="border rounded-md p-4 mb-6 print-block">
          <h2 className="text-lg font-semibold mb-3">Personal Statement</h2>
          <p className="text-sm">{displayValue(profileData.personal_statement)}</p>
        </div>
      )}
      
      {/* Two-column masonry layout with explicit page break control */}
      <div className="grid grid-cols-2 gap-6 print:grid-cols-1">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Academic Information */}
          <div className="border rounded-md p-4 print-block">
            <h2 className="text-lg font-semibold mb-3">Academic Information</h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">School</p>
                <p>{displayValue(getHighSchoolName())}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">GPA</p>
                <p>{displayValue(profileData.academics?.gpa)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-sm text-gray-500">SAT</p>
                  <p>{displayValue(profileData.academics?.sat_total)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">ACT</p>
                  <p>{displayValue(profileData.academics?.act_composite)}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Combine Stats - Updated to clearly show both label and value */}
          {config.showCombineStats && athleteStats.combineScores && athleteStats.combineScores.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Combine Stats</h2>
              <div className="grid grid-cols-2 gap-2">
                {athleteStats.combineScores.map((stat: any, index: number) => (
                  <div key={index}>
                    <p className="text-sm text-gray-500">
                      {formatStatName(stat.test_name || stat.label || stat.stat_name || '')}
                    </p>
                    <p>
                      {displayValue(stat.score)} {displayValue(stat.unit)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Recruiting Preferences */}
          {config.showPreferences && profileData.preferences && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Recruiting Preferences</h2>
              <div className="space-y-2">
                {profileData.preferences.preferred_divisions && profileData.preferences.preferred_divisions.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Preferred Divisions</p>
                    <p>{profileData.preferences.preferred_divisions.join(', ')}</p>
                  </div>
                )}
                
                {profileData.preferences.preferred_locations && profileData.preferences.preferred_locations.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Preferred Locations</p>
                    <p>{profileData.preferences.preferred_locations.join(', ')}</p>
                  </div>
                )}
                
                {profileData.preferences.major_interest && (
                  <div>
                    <p className="text-sm text-gray-500">Major Interest</p>
                    <p>{displayValue(profileData.preferences.major_interest)}</p>
                  </div>
                )}
                
                {profileData.preferences.preferred_school_sizes && profileData.preferences.preferred_school_sizes.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500">Preferred School Sizes</p>
                    <p>{profileData.preferences.preferred_school_sizes.join(', ')}</p>
                  </div>
                )}
                
                {profileData.preferences.max_annual_budget !== undefined && (
                  <div>
                    <p className="text-sm text-gray-500">Maximum Annual Budget</p>
                    <p>${displayValue(profileData.preferences.max_annual_budget)}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Social Media Links */}
          {config.showSocialMedia && profileData.social_media && profileData.social_media.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Social Media</h2>
              <div className="space-y-2">
                {profileData.social_media.map((account: any, index: number) => (
                  <div key={index}>
                    <p className="text-sm text-gray-500">{account.platform}</p>
                    <a 
                      href={buildSocialMediaUrl(account.platform, account.url || account.handle || account.username || '')}
                      className="break-words text-blue-600 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {buildSocialMediaUrl(account.platform, account.url || account.handle || account.username || '')}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Events */}
          {config.showEvents && profileData.events && profileData.events.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Upcoming Events</h2>
              <div className="space-y-2">
                {profileData.events.map((event: any, index: number) => (
                  <div key={index} className="border-b pb-2 last:border-0 last:pb-0">
                    <p className="font-medium">{displayValue(event.event_name || event.title)}</p>
                    <p className="text-sm text-gray-700">{displayValue(event.location)}</p>
                    <p className="text-sm text-gray-500">
                      {event.event_date ? new Date(event.event_date).toLocaleDateString() : 
                       (event.start_date ? parseLocalDate(event.start_date).toLocaleDateString() : '')}
                      {event.end_date ? ` - ${parseLocalDate(event.end_date).toLocaleDateString()}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Sport Stats */}
          {config.showSportStats && athleteStats.sportStats && athleteStats.sportStats.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">{getSportName()} Statistics</h2>
              <div className="grid grid-cols-2 gap-2">
                {athleteStats.sportStats.map((stat: any, index: number) => (
                  <div key={index}>
                    <p className="text-sm text-gray-500">{formatStatName(stat.stat_name)}</p>
                    <p>{displayValue(stat.stat_value)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Athletic Achievements */}
          {config.showAchievements && profileData.athletic_achievements && profileData.athletic_achievements.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Athletic Achievements</h2>
              <ul className="list-disc pl-5 space-y-1">
                {profileData.athletic_achievements.map((achievement: any, index: number) => (
                  <li key={index}>{displayValue(achievement.description)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Academic Achievements */}
          {profileData.academic_achievements && profileData.academic_achievements.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Academic Achievements</h2>
              <ul className="list-disc pl-5 space-y-1">
                {profileData.academic_achievements.map((achievement: any, index: number) => (
                  <li key={index}>{displayValue(achievement.title)}: {displayValue(achievement.description)}</li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Experiences */}
          {config.showExperience && profileData.experiences && profileData.experiences.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">Athletic Experience</h2>
              {profileData.experiences.map((experience: any, index: number) => (
                <div key={index} className="mb-3 last:mb-0">
                  <p className="font-medium">{displayValue(experience.team_name)}</p>
                  <p className="text-sm">{displayValue(experience.organization_name)}</p>
                  <p className="text-sm text-gray-700">
                    {experience.start_date ? parseLocalDate(experience.start_date).toLocaleDateString() : ''} - 
                    {experience.end_date ? parseLocalDate(experience.end_date).toLocaleDateString() : 'Present'}
                  </p>
                  {experience.description && <p className="text-sm mt-1">{experience.description}</p>}
                </div>
              ))}
            </div>
          )}
          
          {/* College Offers */}
          {config.showOffers && profileData.offers && profileData.offers.length > 0 && (
            <div className="border rounded-md p-4 print-block">
              <h2 className="text-lg font-semibold mb-3">College Offers</h2>
              <ul className="space-y-2">
                {profileData.offers.map((offer: any, index: number) => (
                  <li key={index} className="border-b pb-2 last:border-0 last:pb-0">
                    <p className="font-medium">{displayValue(offer.college_name || offer.school_name)}</p>
                    <p className="text-sm text-gray-700">
                      {displayValue(offer.division)} {displayValue(offer.conference)}
                    </p>
                    {offer.date_offered && 
                      <p className="text-sm text-gray-500">Offered: {new Date(offer.date_offered).toLocaleDateString()}</p>
                    }
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      <div className="text-center mt-8 text-xs text-gray-500 print-block">
        <p>For more information, including highlight videos, press, and coach recommendations, please scan the QR code or visit recruitlook.com/profile/{profileData.username || ''}</p>
      </div>
      
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              .flyer-page {
                page-break-inside: avoid;
                break-inside: avoid;
                box-shadow: none;
                padding: 0;
              }
              
              .print-block {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              
              .page-break {
                page-break-before: always !important;
                break-before: always !important;
              }
              
              /* Force sections to stay together */
              .print-block h2,
              .print-block p,
              .print-block div {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
          `
        }}
      />
    </div>
  );
};

export default FlyerTemplate;
